/**
 * Cross-Domain Authentication Helper
 * Utilities for syncing Cognito authentication across different domains.
 *
 * Writes go through the project-wide `EnhancedCookie` (`getCookieStorage()`) using
 * `isGlobal: true` + an explicit `domain` — the same pattern used by `removeAndSetNewCookies`
 * in `src/utils/auth.utils.ts` for sign-in cookies. Reads still use `parse(document.cookie)`
 * directly because we need a bulk multi-cookie sweep, which the keyed EnhancedCookie API
 * doesn't optimise for.
 */

import { datadogRum } from '@datadog/browser-rum'
import { parse } from 'cookie'

import { DD_RUM_ENABLED_FLAG } from '@src/config/env'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ID_TOKEN_KEY } from '@src/quince-site-common/src/services/cartCheckoutService/constants'
import { clearCartStorage } from '@src/quince-site-common/src/utils/cart-storage.utils'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { getCookieStorage, type CookieOptions as EnhancedCookieOptions } from './enhancedCookie'

/**
 * Shape of Cognito token data transferred via cookies between D2C and B2B domains.
 * Mirrors CognitoTokenData from @lastbrand/fe-common/universal-auth — defined locally
 * so TypeScript-eslint can resolve it without package-exports resolution issues.
 */
export interface CognitoTokenData {
  refreshToken: string | null
  idToken: string | null
  accessToken: string | null
  /** Amplify clock drift metadata — optional, regenerated automatically if missing. */
  clockDrift?: string | null
  /** Amplify sign-in metadata — optional, regenerated automatically if missing. */
  signInDetails?: string | null
  userPoolId: string | null
  username: string | null
  keys: {
    refreshTokenKey: string | null
    idTokenKey: string | null
    accessTokenKey: string | null
    clockDriftKey?: string | null
    signInDetailsKey?: string | null
    lastAuthUserKey: string | null
  }
}

const DEFAULT_COOKIE_DOMAIN = '.quince.com' // eslint-disable-line i18next/no-literal-string

function deriveCookieDomain(siteUrl: string): string {
  let hostname: string
  try {
    hostname = new URL(siteUrl).hostname
  } catch {
    return DEFAULT_COOKIE_DOMAIN
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ''
  }

  const firstDot = hostname.indexOf('.')
  if (firstDot === -1) return ''

  return hostname.substring(firstDot) // e.g. .quince.com, .onequince.com, .preview-staging.quince.com
}

export const COOKIE_DOMAIN = deriveCookieDomain(process.env.NEXT_PUBLIC_SITE_URL ?? 'https://www.quince.com/')
export const COOKIE_PREFIX = 'B2B_SS_AUTH_'
export const COOKIE_MAX_AGE = 5 * 60 // 5 minutes (short-lived for security)

/**
 * TTL for the cross-domain logout broadcast cookie. Long enough to cover a same-session tab
 * switch from one origin to the other, short enough that a stale flag never lingers across
 * sessions. After expiry the user may briefly look logged-in on the other origin until the
 * Cognito access token expires — `signOut({ global: true })` already revoked the refresh
 * token so the next refresh will fail.
 */
export const LOGOUT_BROADCAST_MAX_AGE = 60 // seconds

/**
 * MUST stay in sync with web-checkout's `getCrossDomainCookieDomain()` in
 * `utils/cross-domain-auth.utils.ts` — both produce the cookie domain for
 * the shared `B2B_SS_AUTH_*` family (tokens + logout broadcast). A mismatch
 * silently breaks the cross-domain flow: one origin writes on a domain the
 * other origin's consumer can't read, surfacing as the "logged out on D2C,
 * still logged in on checkout" bug. When adding a new hostname pattern
 * (new preview env, brand-domain rollout, etc.) update both functions in
 * the same release.
 *
 * Note: this derivation uses `window.location.hostname` at runtime, while
 * the module-level `COOKIE_DOMAIN` is derived from `NEXT_PUBLIC_SITE_URL`
 * at build time. web-checkout's sibling is purely hostname-driven.
 */
const getEffectiveCookieDomain = (): string => {
  if (typeof window === 'undefined') return COOKIE_DOMAIN

  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ''
  }

  // For preview domains (e.g. branch.preview-staging.quince.com), COOKIE_DOMAIN may be
  // .onequince.com (built from NEXT_PUBLIC_SITE_URL=pre-integ.onequince.com). The browser
  // rejects cookies whose domain is not a suffix of the current hostname, so derive the
  // cookie domain from the actual hostname instead.
  if (hostname.includes('preview-')) {
    const firstDot = hostname.indexOf('.')
    return firstDot !== -1 ? hostname.substring(firstDot) : `.${hostname}`
  }

  return COOKIE_DOMAIN.startsWith('.') ? COOKIE_DOMAIN : `.${COOKIE_DOMAIN}`
}

export const COOKIE_NAMES = {
  REFRESH_TOKEN: `${COOKIE_PREFIX}refresh_token`,
  ID_TOKEN: `${COOKIE_PREFIX}id_token`,
  ACCESS_TOKEN: `${COOKIE_PREFIX}access_token`,
  CLOCK_DRIFT: `${COOKIE_PREFIX}clock_drift`,
  SIGN_IN_DETAILS: `${COOKIE_PREFIX}sign_in_details`,
  USER_POOL_ID: `${COOKIE_PREFIX}user_pool_id`,
  USERNAME: `${COOKIE_PREFIX}username`,
  KEYS: `${COOKIE_PREFIX}keys`,
  /**
   * Cross-domain logout broadcast. Set on the shared cookie domain by either origin's logout
   * flow; consumed by the other origin's root layout to trigger a local sign-out. Listed here
   * so the existing `clearCognitoTokenCookies()` sweep removes any stale broadcast left over
   * during a re-login race (see `cross-domain-logout-b2b.md` → Edge cases → "Race with login").
   */
  LOGOUT_BROADCAST: `${COOKIE_PREFIX}logout`,
} as const

export const B2B_TENANT_COOKIE = 'b2b_tenant' // eslint-disable-line i18next/no-literal-string

/**
 * Builds the EnhancedCookie options shared by every cross-domain write. Always pins
 * `isGlobal: true` (no locale prefix), `path: '/'`, and the explicitly-derived `domain`
 * so the cookie is readable across `*.quince.com` (or the appropriate preview domain).
 *
 * `expiresInSeconds` is converted to an explicit `Date` because EnhancedCookie's numeric
 * `expires` is treated as MINUTES (and falls back to 400 days when nullish), neither of
 * which matches the second-precision TTLs we use here (e.g. 60s broadcast).
 */
const buildCrossDomainCookieOptions = (expiresInSeconds: number): EnhancedCookieOptions => {
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:'
  const effectiveDomain = getEffectiveCookieDomain()
  return {
    isGlobal: true,
    path: '/',
    sameSite: 'lax', // eslint-disable-line i18next/no-literal-string -- HTTP attribute
    expires: new Date(Date.now() + expiresInSeconds * 1000),
    ...(isSecure && { secure: true }),
    ...(effectiveDomain && { domain: effectiveDomain }),
  }
}

/**
 * Single choke-point for all cross-domain cookie writes. Delegates to the same
 * `EnhancedCookie` utility used by sign-in flows (see `removeAndSetNewCookies` in
 * `src/utils/auth.utils.ts`) so we don't have a parallel `document.cookie =` path
 * to keep in sync with the project's cookie conventions.
 */
const writeCrossDomainCookie = (name: string, value: string, expiresInSeconds: number): void => {
  if (typeof window === 'undefined') return
  const options = buildCrossDomainCookieOptions(expiresInSeconds)
  getCookieStorage().setCookieLocalized(name, value, options)
}

/** Expires a cross-domain cookie via EnhancedCookie's `removeCookieLocalized` helper. */
const expireCrossDomainCookie = (name: string): void => {
  if (typeof window === 'undefined') return
  const effectiveDomain = getEffectiveCookieDomain()
  getCookieStorage().removeCookieLocalized(name, {
    isGlobal: true,
    path: '/',
    ...(effectiveDomain && { domain: effectiveDomain }),
  })
}

/**
 * RUM action tag shared by every cross-domain logout event so dashboards can group on a
 * single `flow` value regardless of which origin (D2C / B2B) emitted the action.
 */
const CD_LOGOUT_FLOW = 'CROSS_DOMAIN_LOGOUT' // eslint-disable-line i18next/no-literal-string -- diagnostic tag, never user-facing

/**
 * Emits a Datadog RUM custom action for the cross-domain logout flow. Wrapped in defensive
 * guards (`typeof window`, `DD_RUM_ENABLED_FLAG`, try/catch) because telemetry MUST NEVER
 * break the auth flow it's observing — a thrown error from `addAction` would propagate up
 * into a logout/login path and cause exactly the bug we're trying to debug.
 */
const reportCdLogoutAction = (name: string, ctx: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return
  if (!DD_RUM_ENABLED_FLAG) return
  try {
    datadogRum.addAction(name, { flow: CD_LOGOUT_FLOW, ...ctx })
  } catch {
    // Intentionally swallowed: Datadog RUM should never affect auth correctness.
  }
}

/**
 * Sets Cognito tokens in cookies for cross-domain access
 * Cookies are set with COOKIE_DOMAIN to be accessible across subdomains
 */
export const setCognitoTokenCookies = (tokenData: CognitoTokenData): void => {
  if (typeof document === 'undefined') {
    throw new Error('setCognitoTokenCookies can only be used in browser environment')
  }

  // A successful login invalidates any pending logout broadcast — otherwise a fast
  // logout-then-login on D2C would still log the user out the next time B2B is opened
  // (within the 60s broadcast TTL). See cross-domain-logout-b2b.md → "Race with login".
  // Capture the pre-clear state so we can emit a Datadog action ONLY when we actually
  // swallowed a broadcast — a no-op clear on the common login path would just be noise.
  const broadcastWasPresentAtLogin = hasLogoutBroadcastCookie()
  clearLogoutBroadcastCookie()
  if (broadcastWasPresentAtLogin) {
    /* eslint-disable i18next/no-literal-string -- diagnostic action name + discriminator, never user-facing */
    reportCdLogoutAction('cd-logout.broadcast.swallowed', {
      reason: 'race-with-login',
      hostname: window.location.hostname,
    })
    /* eslint-enable i18next/no-literal-string */
  }

  const setCookie = (name: string, value: string | null | undefined) => {
    if (value) writeCrossDomainCookie(name, value, COOKIE_MAX_AGE)
  }

  setCookie(COOKIE_NAMES.REFRESH_TOKEN, tokenData.refreshToken)
  setCookie(COOKIE_NAMES.ID_TOKEN, tokenData.idToken)
  setCookie(COOKIE_NAMES.ACCESS_TOKEN, tokenData.accessToken)
  setCookie(COOKIE_NAMES.CLOCK_DRIFT, tokenData.clockDrift)
  setCookie(COOKIE_NAMES.SIGN_IN_DETAILS, tokenData.signInDetails)
  setCookie(COOKIE_NAMES.USER_POOL_ID, tokenData.userPoolId)
  setCookie(COOKIE_NAMES.USERNAME, tokenData.username)
  writeCrossDomainCookie(COOKIE_NAMES.KEYS, JSON.stringify(tokenData.keys), COOKIE_MAX_AGE)
}

/**
 * Reads Cognito tokens from cookies
 * Returns null if cookies are not found
 */
export const getCognitoTokenCookies = (): CognitoTokenData | null => {
  if (typeof document === 'undefined') {
    return null
  }

  // eslint-disable-next-line @quince/no-direct-cookie, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- cross-domain cookies require raw document.cookie
  const cookies: Record<string, string> = parse(document.cookie)

  const hasSyncCookies = Object.keys(cookies).some(key => key.startsWith(COOKIE_PREFIX))

  if (!hasSyncCookies) {
    return null
  }

  let keys: CognitoTokenData['keys'] = {
    refreshTokenKey: null,
    idTokenKey: null,
    accessTokenKey: null,
    clockDriftKey: null,
    signInDetailsKey: null,
    lastAuthUserKey: null,
  }

  try {
    const keysRaw = cookies[COOKIE_NAMES.KEYS]
    if (keysRaw) {
      keys = JSON.parse(keysRaw) as CognitoTokenData['keys']
    }
  } catch (error) {
    console.error('Error parsing keys from cookie:', error)
  }

  const result: CognitoTokenData = {
    refreshToken: cookies[COOKIE_NAMES.REFRESH_TOKEN] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing -- empty string from cookie means absent
    idToken: cookies[COOKIE_NAMES.ID_TOKEN] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    accessToken: cookies[COOKIE_NAMES.ACCESS_TOKEN] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    clockDrift: cookies[COOKIE_NAMES.CLOCK_DRIFT] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    signInDetails: cookies[COOKIE_NAMES.SIGN_IN_DETAILS] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    userPoolId: cookies[COOKIE_NAMES.USER_POOL_ID] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    username: cookies[COOKIE_NAMES.USERNAME] || null, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    keys,
  }

  return result
}

/** SSR idToken cookie TTL — 1 hour to match Cognito access-token lifetime. */
const SSR_ID_TOKEN_MAX_AGE = 60 * 60 // seconds

/**
 * Sets the `idToken` cookie consumed by SSR middleware (withProgramAccess).
 * Must be called after restoreCognitoTokens so the next SSR request is authenticated.
 *
 * The SSR idToken cookie is set with a 1-hour TTL to match Cognito token
 * expiration and limit XSS exposure window.
 */
export const setIdTokenCookieForSSR = (idToken: string): void => {
  if (typeof document === 'undefined') return
  writeCrossDomainCookie(ID_TOKEN_KEY, idToken, SSR_ID_TOKEN_MAX_AGE) // @i18n-ignore
}

/**
 * Clears the short-lived Cognito sync cookies (B2B_SS_AUTH_*).
 * Does NOT clear the SSR idToken cookie — that is managed separately because
 * clearCognitoTokenCookies is called immediately after token restoration in
 * B2BRootLayout to clean up sync cookies, while the idToken must persist for SSR.
 */
export const clearCognitoTokenCookies = (): void => {
  if (typeof document === 'undefined') return
  // Capture the pre-sweep state so we only emit a Datadog action when an actual broadcast
  // cookie is about to be destroyed by the COOKIE_NAMES sweep. Avoids noisy actions on the
  // common post-restore cleanup path where no broadcast was ever set.
  const broadcastWasPresentBeforeSweep = hasLogoutBroadcastCookie()
  Object.values(COOKIE_NAMES).forEach(expireCrossDomainCookie)
  if (broadcastWasPresentBeforeSweep) {
    /* eslint-disable i18next/no-literal-string -- diagnostic action name + discriminator, never user-facing */
    reportCdLogoutAction('cd-logout.broadcast.swallowed', {
      reason: 'post-restore-sweep',
      hostname: window.location.hostname,
    })
    /* eslint-enable i18next/no-literal-string */
  }
}

/**
 * Clears all B2B session cookies including SSR idToken and tenant cookie.
 * Use during logout to fully clean up authentication state.
 */
export const clearAllB2BAuthCookies = (): void => {
  clearCognitoTokenCookies()
  if (typeof document === 'undefined') return
  // Only clear the idToken SSR cookie — B2B_TENANT_COOKIE is a routing/config
  // cookie that must persist across login/logout cycles. On localhost the tenant
  // cookie is the only way getB2BTenant can identify the B2B storefront; clearing
  // it breaks logged-out page loads. On deployed environments it's harmless.
  expireCrossDomainCookie(ID_TOKEN_KEY) // @i18n-ignore
}

/**
 * Sets the cross-domain logout broadcast cookie on the shared parent domain. The other
 * origin's root layout polls for this cookie on mount/focus and runs its own sign-out when
 * it appears, so a logout on D2C propagates to B2B (and vice versa) without same-origin
 * messaging. Pairs with `signOut({ global: true })` which revokes the refresh token at
 * Cognito — together they bound the cross-domain "still logged in" window to one
 * access-token TTL (or 60s, whichever is shorter).
 */
export const setLogoutBroadcastCookie = (): void => {
  if (typeof document === 'undefined') return
  writeCrossDomainCookie(COOKIE_NAMES.LOGOUT_BROADCAST, '1', LOGOUT_BROADCAST_MAX_AGE) // @i18n-ignore

  // Verify the browser actually persisted the cookie. Common silent-rejection causes:
  //   - Domain attribute is not a suffix of the current hostname (preview deploys, etc.)
  //   - Third-party-cookie blocking / ITP on Safari
  //   - Secure attribute on a non-HTTPS origin
  // Any of these produce the user-visible bug "I logged out on D2C but B2B stayed logged
  // in" with zero local evidence — RUM is the only place we can see it on real devices.
  const writeVerified = hasLogoutBroadcastCookie()
  const hostname = window.location.hostname
  const effectiveDomain = getEffectiveCookieDomain()

  // eslint-disable-next-line i18next/no-literal-string -- diagnostic action name, never user-facing
  reportCdLogoutAction('cd-logout.broadcast.emit', {
    hostname,
    effectiveDomain,
    secure: window.location.protocol === 'https:',
    writeVerified,
  })

  if (!writeVerified) {
    // eslint-disable-next-line i18next/no-literal-string -- diagnostic error message, never user-facing
    captureException(new Error('cd-logout: broadcast cookie write rejected by browser'), {
      module: MODULES.UNIVERSAL_AUTH,
      feature: FEATURES.UNIVERSAL_AUTH,
      additionalTags: {
        flow: CD_LOGOUT_FLOW,
        hostname,
        effectiveDomain,
      },
    })
  }
}

/** True if a logout broadcast cookie is currently present on this origin. */
export const hasLogoutBroadcastCookie = (): boolean => {
  if (typeof document === 'undefined') return false
  // eslint-disable-next-line @quince/no-direct-cookie, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call -- cross-domain cookies require raw document.cookie
  const cookies: Record<string, string> = parse(document.cookie)
  return Boolean(cookies[COOKIE_NAMES.LOGOUT_BROADCAST])
}

/**
 * Clears the broadcast cookie immediately after consumption so the local logout effect
 * doesn't fire repeatedly (e.g. after the page reload that follows the local sign-out).
 */
export const clearLogoutBroadcastCookie = (): void => {
  if (typeof document === 'undefined') return
  expireCrossDomainCookie(COOKIE_NAMES.LOGOUT_BROADCAST)
}

/**
 * Click-time / pre-redirect call from any sign-in entry point (Google OAuth, email/password,
 * sign-up, OTP-verify, post-signin side-effects). Clears a stale cross-domain logout broadcast
 * so `useCrossDomainLogoutSync` can't race the in-flight Cognito sign-in and trigger
 * `signOut({ global: true })` mid-flow — which would revoke the fresh session at the IdP and
 * leave the user stuck.
 *
 * Centralised here so a future sign-in entry point (Apple ID, magic link, passkey) can't
 * silently regress this guard.
 */
export const clearStaleLogoutBroadcastForSignIn = (): void => {
  if (hasLogoutBroadcastCookie()) clearLogoutBroadcastCookie()
}

/**
 * Per-user data cleanup ritual shared by every B2B-self-serve **logout** entry point —
 * the originator (`b2b-header.handleLogoutClick`) AND the cross-domain consumer
 * (`b2b-root-layout`'s broadcast handler). Mirrors the subset of D2C `logoutUser`'s
 * cleanup that actually applies to B2B-self-serve:
 *
 *   - `cart_id` + `checkout_id` from localStorage — without this, the post-logout
 *     reload renders the previous user's cart icon count and a cart fetch by the
 *     stale id can return their cart contents.
 *   - `lb_customer_id` + `_lb_customer_email` cookies — without this, the next
 *     guest session's analytics events would be attributed to the previous user.
 *
 * Deliberately omits `removeWishlist...` and `removeRecentlyViewed...` from D2C's
 * `logoutUser` because B2B-self-serve has no wishlist or recently-viewed feature —
 * calling them would just no-op against keys the storefront never writes.
 *
 * **DO NOT call this from any sign-in / sign-up entry point.** Cart-merge on D2C
 * (and the upcoming B2B cart-merge) reads the guest `checkout_id` from localStorage
 * during login side-effects (`asyncAuthDownstream` / `synchronousAuthDownstream` in
 * `Auth/hooks/utils.ts`). Wiping `cart_id` on the sign-in path would silently drop the
 * guest cart before merge. Sign-in flows must use `clearStaleLogoutBroadcastForSignIn()`
 * instead — it clears only the broadcast cookie and leaves user-data intact.
 *
 * Centralised alongside `clearStaleLogoutBroadcastForSignIn` so a future B2B logout
 * entry point (session-expiry forced logout, end-impersonation, etc.) can't
 * silently regress this privacy/correctness guard.
 *
 * Sibling on the checkout origin: `clearWebCheckoutLogoutUserData()` in
 * `web-checkout/utils/cross-domain-auth.utils.ts`. Both helpers must stay
 * in lockstep — when a new privacy-bound key (analytics cookie, identity
 * cache, customer payload, cart linkage) is added on either origin,
 * update both. The checkout sibling clears a parallel surface
 * (`checkout_data` blob with the in-flight `checkoutAuthCode` token, plus
 * the `cart::*` / `new::cart::*` linkage keys) — without that, the
 * data-holder's LS-overrides-URL bootstrap on the next page load could
 * silently re-bind a different user to the previous user's checkout.
 */
export const clearB2BLogoutUserData = (): void => {
  if (typeof window === 'undefined') return
  clearCartStorage()
  getCookieStorage().removeCookieLocalized(ANALYTIC_COOKIES.LB_CUSTOMER_ID, { isGlobal: true })
  getCookieStorage().removeCookieLocalized(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL, { isGlobal: true })
}
